import type { Money, SlimModel } from '@cplug/frontend-core'
import type { Peripheral } from '../../peripherals'
import type { PrintPointRedirect } from '../../peripherals/models/PrintPointRedirect'
import type { ServerApp } from './App'
import type { SmartMenuRegistrationField } from './SmartMenuRegistrationField'

import type { BaseCustomization } from '~/domain/shared/customization/models/Customization'
import type { Kds } from '~/domain/dashboard/fisical-operations/kds'

export const DefaultPrimaryColorHex = '#000'
export const DefaultBackgroundColorHex = '#f0f0f0'

export enum CustomerType {
  FullRegistration = 'full_registration',
  AskCustomerName = 'ask_customer_name'
}

export enum TotemSubtype {
  Default = 'default',
  SelfCheckout = 'selfcheckout',
  HybridSelfCheckout = 'selfcheckout_hybrid',
  OrdersheetSelfCheckout = 'selfcheckout_ordersheet',
  FullSelfCheckout = 'selfcheckout_full',
  TAPP = 'tapp',
  Scale = 'scale'
}

export enum TotemFulfillmentType {
  EatIn = 'eat_in',
  TakeOut = 'take_out',
  Delivery = 'delivery'
}

export enum InvoiceEmissionType {
  Online = 'online',
  Contingency = 'contingency'
}

export interface ThemeSetting {
  primaryColor?: string
  secondaryColor?: string
}

export interface WelcomeScreenSetting {
  welcomeMessage: string
  finishMessage: string
}

export interface VpnSetting {
  terminal?: string
  id?: string
  cnpj_integrator?: string
  cnpj_client?: string
  ip?: string
  otp?: string
}

export interface TotemUseParamsSetting {
  isRequestTableIdentifier: boolean
  isHiddenObservation: boolean
  isHiddenConsumptionLocation: boolean
  isHiddenSaleNumber: boolean
  isAcceptDiscountVoucher: boolean
  isDisplayQuantitySelector: boolean
  isShowPriceWithDiscount: boolean
  isEnableTranslate: boolean
  isRequestCustomerName: boolean
}

export interface TotemFiscalSetting {
  isGenerateInvoice: boolean
  generateInvoiceType: InvoiceEmissionType
  isDocumentInInvoice: boolean
  isSendInvoiceToEmail: boolean
}

export interface TotemPeripheralsSetting {
  sat?: Peripheral
  printPoint?: Peripheral
  printPointExpedition?: Peripheral
  scale?: Peripheral
}

export interface TotemAppSetting {
  server?: ServerApp
  subtype: TotemSubtype
  selfOrder: boolean
  baseProduct?: SlimModel
  welcomeScreen: WelcomeScreenSetting
  theme: ThemeSetting
  useParams: TotemUseParamsSetting
  fiscal: TotemFiscalSetting
  peripherals?: TotemPeripheralsSetting
  customization?: BaseCustomization
  vpn?: VpnSetting
  isGenerateOfflineSaleNumber?: boolean
  isGenerateAndPrintOfflineSale?: boolean
}

export interface DigitalMenuUseParamsSetting {
  isHiddenButtonCloseAccount: boolean
  isHiddenButtonWaiterCall: boolean
}

export interface DigitalMenuAppSetting {
  theme: ThemeSetting
  welcomeScreen: WelcomeScreenSetting
  useParams: DigitalMenuUseParamsSetting
  customization?: BaseCustomization
}

export interface ServerAppSetting {
  ip?: string
  useHttp?: boolean
}

export interface PdvMobileAppSetting {
  server?: ServerApp
  sat?: Peripheral
  isGenerateOfflineSaleNumber?: boolean
  isGenerateAndPrintOfflineSale?: boolean
  vpn?: VpnSetting
}

export interface MobileAppSetting {
  redirectEnabled?: boolean
  printPointRedirects?: PrintPointRedirect[]
  pointOfSale?: SlimModel
}

export enum DesktopModuleKind {
  PasswordPanel = 'PASSWORD_PANEL',
  Cashier = 'CASHIER',
  KDS = 'KDS',
  CustomerService = 'CUSTOMER_SERVICE'
}

interface DesktopModuleBase {
  kind: DesktopModuleKind
}

interface DesktopKdsSetting {
  kds: Kds
}

export interface DesktopModuleKDS extends DesktopModuleBase {
  kind: DesktopModuleKind.KDS
  settings: DesktopKdsSetting
}

export type DesktopModule = DesktopModuleBase | DesktopModuleKDS

export interface DesktopAppSetting {
  server?: ServerApp
  modules: DesktopModule[]
  redirectEnabled?: boolean
  printPointRedirects?: PrintPointRedirect[]
  peripherals?: {
    sat?: Peripheral
    printPoint?: Peripheral
    scale?: Peripheral
  }
  vpn?: VpnSetting
}

// Store App Settings
export interface StoreBasicInfo {
  name: string
  description?: string
  customURL?: string
  timezone?: string
  /** Endereço desta unidade dentro do domínio compartilhado (`https://{domain}/{slug}`). */
  slug?: string | null
}

export enum StoreCatalogLayout {
  List = 'list',
  Feed = 'feed'
}

export interface StoreThemeSetting {
  primaryColor?: string
  secondaryColor?: string
  faviconUrl?: string
  coverImageUrl?: string
  catalogLayout?: StoreCatalogLayout
}

export enum StoreSocialMediaKind {
  Facebook = 'facebook',
  Instagram = 'instagram',
  LinkedIn = 'linkedin',
  Pinterest = 'pinterest',
  TikTok = 'tiktok',
  Twitter = 'twitter',
  Vimeo = 'vimeo',
  YouTube = 'youtube'
}

export interface StoreSocialMedia {
  kind: StoreSocialMediaKind
  url: string
}

export enum StoreContactType {
  Email = 'email',
  Phone = 'phone',
  WhatsApp = 'whatsapp'
}

export interface StoreContact {
  type: StoreContactType
  value: string
}

export interface StoreAppSettingsContact {
  channels: StoreContact[]
  notes?: string
}

export interface StoreFulfillmentSetting {
  fulfillmentsAvailable: string[]
  minPickupMinutes: number
  minPickupOrderValue: Money
  minDeliveryOrderValue: Money
  freeDeliveryFeeFromOrder: Money
}

export interface StoreAppSetting {
  id?: number
  basicInfo: StoreBasicInfo
  theme: StoreThemeSetting
  contact: StoreAppSettingsContact
  socialMedia: StoreSocialMedia[]
  fulfillment?: StoreFulfillmentSetting
}

export interface SmartMenuThemeSetting {
  primaryColor?: string
  secondaryColor?: string
  logo?: string
}

export interface SmartMenuRegistrationConfigsSetting {
  messageSuccessRegistration?: string
  showQrcode?: boolean
  showBarcode?: boolean
}

export interface SmartMenuRegistrationSetting {
  active?: boolean
  url?: string | null
  fields?: SmartMenuRegistrationField[]
  configs?: SmartMenuRegistrationConfigsSetting
}

export interface SmartMenuDigitalMenuConfigsSetting {
  showImage?: boolean
  showPriceSymbol?: boolean
  showDecimal?: boolean
  enableOrder?: boolean
  enableServiceTax?: boolean
  enableTableOrder?: boolean
  requireTableNumber?: boolean
  enableCustomerService?: boolean
  orderNeedsCustomerServiceOpened?: boolean
  showRequestConfirmation?: boolean
  enableSubcategories?: boolean
  requestConfirmationMessage?: string
  requestConfirmationConfirmButtonText?: string
  requestConfirmationCloseButtonText?: string
  reorderMessage?: string
}

export interface SmartMenuDigitalMenuSetting {
  active?: boolean
  url?: string | null
  configs?: SmartMenuDigitalMenuConfigsSetting
}

export interface SmartMenuFunctionalitiesSetting {
  registration?: SmartMenuRegistrationSetting
  digitalmenu?: SmartMenuDigitalMenuSetting
}

export interface SmartMenuAppSetting {
  pageTitle?: string
  smartMenuUrl?: string
  theme?: SmartMenuThemeSetting
  functionalities?: SmartMenuFunctionalitiesSetting
}

export interface KdsMobileAppSetting {
  kdsId?: number
}
